import React, { useEffect, useState } from 'react';
import {
  CheckCircleOutlined,
  EditOutlined,
  FileSearchOutlined,
  RobotOutlined,
} from '@ant-design/icons';
import ThinkingProcessResourceLine from './ThinkingProcessResourceLine.jsx';
import { resolveSkillIcon } from '../../capabilities/capabilityIcons.jsx';

const ACTIVITY_ICONS = new Map([
  ['resource_read', <FileSearchOutlined />],
  ['artifact_update', <EditOutlined />],
  ['artifact_admission', <CheckCircleOutlined />],
  ['decision', <CheckCircleOutlined />],
  ['capability', <RobotOutlined />],
  ['observation', <FileSearchOutlined />],
  ['commit', <EditOutlined />],
]);
const ACTIVITY_STATUS_LABELS = new Map([
  ['pending', '待执行'],
  ['running', '进行中'],
  ['partial', '部分完成'],
  ['failed', '失败'],
  ['waiting_user', '等待确认'],
  ['blocked', '已阻塞'],
  ['cancelled', '已取消'],
]);
const EXPANDED_STEP_STATUSES = new Set(['running', 'waiting_user', 'blocked', 'failed']);

const activityIcon = (activity = {}) => (
  activity.kind === 'capability' && activity.skillId
    ? resolveSkillIcon(activity)
    : ACTIVITY_ICONS.get(activity.kind) ?? <RobotOutlined />
);

function ActivityRow({ activity, onUICardAction }) {
  const status = String(activity.status || '').trim();
  return (
    <div className={`flare-thinking-activity is-${activity.kind} is-status-${status || 'unknown'}`}>
      <span aria-hidden="true" className="flare-thinking-activity-icon">
        {activityIcon(activity)}
      </span>
      <div className="flare-thinking-activity-body">
        <span className="flare-thinking-activity-heading">
          <span className="flare-thinking-activity-label">{activity.label}</span>
          {ACTIVITY_STATUS_LABELS.has(status) ? (
            <span className="flare-thinking-activity-status">{ACTIVITY_STATUS_LABELS.get(status)}</span>
          ) : null}
        </span>
        {activity.detail ? (
          <span className="flare-thinking-activity-detail">{activity.detail}</span>
        ) : null}
        {activity.resources.length > 0 ? (
          <div className="flare-thinking-activity-resources">
            {activity.resources.map((resource, index) => (
              <ThinkingProcessResourceLine
                fallbackLine={resource.line}
                key={`${activity.key}:resource:${index}`}
                onUICardAction={onUICardAction}
                resource={resource}
              />
            ))}
          </div>
        ) : null}
        <ActivityResultEvidence activity={activity} onUICardAction={onUICardAction} />
      </div>
    </div>
  );
}

function ActivityResultEvidence({ activity, onUICardAction }) {
  return (
    <>
      {activity.basis?.length > 0 ? (
        <ActivityEvidence title="依据" entries={activity.basis} onUICardAction={onUICardAction} />
      ) : null}
      {activity.evidence?.length > 0 ? (
        <ActivityEvidence title="读取" entries={activity.evidence} onUICardAction={onUICardAction} />
      ) : null}
      {activity.outcome ? (
        <ActivityOutcome outcome={activity.outcome} onUICardAction={onUICardAction} />
      ) : null}
    </>
  );
}

function ActivityEvidence({ entries, onUICardAction, title }) {
  return (
    <div className="flare-thinking-activity-evidence">
      <span>{title}</span>
      {entries.map((entry) => (
        <ThinkingProcessResourceLine
          fallbackLine={[entry.label, entry.detail].filter(Boolean).join(' · ')}
          key={`${entry.kind}:${entry.referenceId}`}
          onUICardAction={onUICardAction}
          resource={{ label: entry.label, meta: entry.detail, action: entry.action }}
        />
      ))}
    </div>
  );
}

function ActivityOutcome({ outcome, onUICardAction }) {
  const line = [outcome.label, outcome.detail].filter(Boolean).join(' · ');
  return (
    <div className="flare-thinking-activity-outcome">
      <span>结果</span>
      {line ? <span>{line}</span> : null}
      {outcome.references?.length > 0 ? (
        <ActivityEvidence entries={outcome.references} onUICardAction={onUICardAction} title="" />
      ) : null}
    </div>
  );
}

export default function ThinkingProcessActivities({ group, onUICardAction }) {
  const stepStatus = String(group?.stepStatus || '').trim();
  const shouldExpand = EXPANDED_STEP_STATUSES.has(stepStatus);
  const [open, setOpen] = useState(shouldExpand);
  useEffect(() => setOpen(shouldExpand), [shouldExpand, stepStatus]);
  if (!group?.activities?.length) return null;
  return (
    <details
      className={`flare-thinking-activity-group is-${stepStatus || 'unknown'}`}
      onToggle={(event) => setOpen(event.currentTarget.open)}
      open={open}
    >
      <summary>{group.summary || '查看执行记录'}</summary>
      <div className="flare-thinking-activity-list">
        {group.activities.map((activity) => (
          <ActivityRow
            activity={activity}
            key={activity.key}
            onUICardAction={onUICardAction}
          />
        ))}
      </div>
    </details>
  );
}
